import React from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { useEffectOnce } from 'react-use';

import { OffScreenDetector, OffScreenDetectorChildrenProps } from '@dbt-labs/sourdough';

import { CustomModal } from './components/CustomModal';
import { NotFoundPage } from './components/ErrorScreens/NotFound';
import { clearOldFeatures } from './components/NewFeaturePing';
import { PagePadding } from './components/PageLayout';
import { Toaster } from './components/Toaster';
import { AccountNavigationProvider } from './context/AccountNavigation/AccountNavigationProvider';
import { DbtCloudProjectProvider } from './context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from './context/DbtCloudProvider';
import { FilterProvider } from './context/FilterProvider';
import { useModalProvider } from './context/ModalProvider';
import { usePanel } from './context/PanelProvider';
import { useReloadPage } from './hooks/useReloadPage';
import { AccountLineagePage } from './pages/account/accountLineage/AccountLineagePage';
import { AssetDetailsPage } from './pages/account/catalog/details/AssetDetailsPage';
import { ColumnLineagePage } from './pages/account/project/columnLineage/ColumnLineagePage';
import { PerformancePage } from './pages/account/project/performance/PerformancePage';
import { ProjectLineagePage } from './pages/account/project/projectLineage/ProjectLineagePage';
import { ProjectOverviewPage } from './pages/account/project/ProjectOverviewPage';
import { RecommendationsPage } from './pages/account/project/recommendations/RecommendationsPage';
import { PackageFilterPage } from './pages/account/project/resource/components/FilterPages/PackageFilterPage';
import { SchemaFilterPage } from './pages/account/project/resource/components/FilterPages/SchemaFilterPage';
import { SourceCollectionPage } from './pages/account/project/resource/details/components/DetailPages/SourceCollectionPage';
import { ResourceDetailsPage } from './pages/account/project/resource/details/ResourceDetailsPage';
import { ResourceFilterPage } from './pages/account/project/resource/ResourceFilterPage';
import { SearchPage } from './pages/account/project/search/SearchPage';
import { AccountSearchPage } from './pages/account/search/AccountSearchPage';
import { MultiProjectNavPanel } from './panel/MultiProjectNavPanel';
import { ProjectNavPanel } from './panel/ProjectNavPanel';
import { SinglePaneNavPanel } from './panel/SinglePaneNavPanel';
import { EnvironmentRedirect, ProjectRedirect } from './util/customRedirect';

const SelectRedirect: React.FC = () => {
  const { search, hash } = useLocation();
  return <Navigate to={`../search/${search}${hash}`} replace />;
};

const AppWrapper: React.FC = () => {
  const {
    isVisible: isModalVisible,
    title: modalTitle,
    content: modalContent,
    modalType,
  } = useModalProvider();
  return (
    <>
      <FilterProvider>
        <AccountNavigationProvider>
          <OffScreenDetector>
            {({
              scrollContainerRef,
              OffScreenDisplay,
            }: OffScreenDetectorChildrenProps) => (
              <>
                <div className="relative">
                  <OffScreenDisplay />
                </div>
                <PageContainer scrollContainerRef={scrollContainerRef} />
              </>
            )}
          </OffScreenDetector>
        </AccountNavigationProvider>
      </FilterProvider>
      <Toaster />
      <CustomModal
        isModalVisible={isModalVisible}
        title={modalTitle}
        children={modalContent}
        modalType={modalType}
      />
    </>
  );
};

type ExplorerRoutesProps = {
  isVerticalSlice: boolean;
};

export const ExplorerRoutes = ({ isVerticalSlice }: ExplorerRoutesProps) => {
  useReloadPage(isVerticalSlice);
  useEffectOnce(() => {
    clearOldFeatures(localStorage);
  });
  const { isSinglePaneView } = usePanel();
  const { hasAccountFeature } = useDbtCloudAccount();

  const isCllEnabled = hasAccountFeature('explorerCll');
  const isCatalogEnabled = hasAccountFeature('catalogIngestion');
  const isCatalogPreviewEnabled = hasAccountFeature('explorerAccountUI');

  return (
    <Routes>
      <Route path={isSinglePaneView ? '*' : ':accountId/*'} Component={AppWrapper}>
        <Route path="" Component={!isSinglePaneView ? MultiProjectNavPanel : null}>
          <Route path="" Component={AccountLineagePage} />
          {isCatalogEnabled && (
            <Route
              path="catalog/:assetId"
              element={
                <PagePadding>
                  <AssetDetailsPage />
                </PagePadding>
              }
            />
          )}
          <Route
            path={
              isSinglePaneView
                ? 'environments/:environmentType/'
                : 'projects/:projectId/environments/:environmentType/'
            }
            Component={DbtCloudProjectProvider}
          >
            <Route path={'lineage/'} Component={ProjectLineagePage} />
            {isCllEnabled && (
              <Route path={'columnlineage/'} Component={ColumnLineagePage} />
            )}
            <Route
              path={'*'}
              Component={
                isSinglePaneView
                  ? SinglePaneNavPanel
                  : isCatalogPreviewEnabled
                    ? null
                    : ProjectNavPanel
              }
            >
              {hasAccountFeature('explorerModelPerformance') && (
                <Route
                  path="performance/"
                  element={
                    <PagePadding>
                      <PerformancePage />
                    </PagePadding>
                  }
                />
              )}
              {hasAccountFeature('explorerRecommendations') && (
                <Route
                  path="recommendations/"
                  element={
                    <PagePadding>
                      <RecommendationsPage />
                    </PagePadding>
                  }
                />
              )}
              {/* Redirect legacy select/ path to search/ */}
              <Route path="select/*" element={<SelectRedirect />} />
              <Route path="search/" Component={SearchPage} />
              <Route
                path="details/:dbtUniqueId/"
                element={
                  <PagePadding>
                    <ResourceDetailsPage />
                  </PagePadding>
                }
              />
              <Route
                path="resource/source/:sourceName/"
                element={
                  <PagePadding>
                    <SourceCollectionPage />
                  </PagePadding>
                }
              />
              <Route
                path="resource/:resourceType/"
                element={
                  <PagePadding>
                    <ResourceFilterPage />
                  </PagePadding>
                }
              />
              <Route
                path="package/:packageName/"
                element={
                  <PagePadding>
                    <PackageFilterPage />
                  </PagePadding>
                }
              />
              <Route
                path="schema/:databaseName/:schemaName/"
                element={
                  <PagePadding>
                    <SchemaFilterPage />
                  </PagePadding>
                }
              />
              <Route
                path=""
                element={
                  <PagePadding>
                    <ProjectOverviewPage />
                  </PagePadding>
                }
              />
            </Route>
          </Route>
        </Route>
        {isCatalogPreviewEnabled && (
          <Route path="search/" Component={AccountSearchPage} />
        )}
        <Route path="projects/:projectId/*" Component={ProjectRedirect} />
        <Route path="environments/:environmentId/*" Component={EnvironmentRedirect} />
        <Route path="*" Component={NotFoundPage} />
      </Route>
    </Routes>
  );
};

declare global {
  interface Window {
    toggleDarkModeTesting?: () => void;
  }
}

interface PageContainerProps {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}

const PageContainer: React.FC<PageContainerProps> = ({ scrollContainerRef }) => {
  return (
    <main
      className="relative h-full w-full bg-bgMain text-fgMain"
      ref={scrollContainerRef as React.RefObject<HTMLDivElement>}
    >
      <Outlet />
    </main>
  );
};
